import { useState } from "react";
import { Controller } from "react-hook-form";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import ImageIcon from "@mui/icons-material/Image";
import AddIcon from "@mui/icons-material/Add";
import CardContainer from "../../../../components/Card/CardContainer";
import CardMedia from "../../../../components/Media/CardMedia";
import ModalMedia from "../../../../components/Modal/ModalMedia";
import UploadFileButton from "../../../../components/Buttons/UploadFileButton";
import Button from "../../../../components/Buttons/Button";
import SectionHeader from "./SectionHeader";
import { useDispatchApp } from "../../../../lib/redux";
import { getPathDatabaseByRole } from "../../../../actions/auth";
import { useTranslationApp } from "../../../../lib/i18next";
import {
  BUTTON_UPLOAD_LOGO,
  BUTTON_CHANGE_LOGO,
  BUTTON_DELETE_LOGO,
  LOGO,
  LOGO_DESCRIPTION,
} from "../../../../i18n/keysTranslations";
import {
  LOGO_IMAGE_FORMATS,
  FONT_SIZE_BUTTON,
} from "../../../../utils/constants";
import { INPUT_COLOR_BORDER } from "../../../../utils/colors";
import { useBrandSetupReadOnly } from "../context/brandSetupReadOnlyContext";

const LogoPreview = ({ logoUrl, onClick }) => {
  return (
    <Box
      sx={{
        display: "inline-block",
      }}
    >
      <CardMedia
        url={logoUrl}
        sx={{
          cursor: onClick ? "pointer" : "default",
          objectFit: "contain",
          objectPosition: "center",
          borderRadius: 2,
          height: 200,
          width: 200,
          maxHeight: 250,
          maxWidth: "100%",
          backgroundColor: "action.hover",
          "&:hover": {
            filter: "brightness(0.75)",
          },
        }}
        onClick={onClick}
      />
    </Box>
  );
};

const LogoPlaceholder = ({ onUploadClick, readOnly = false }) => {
  return (
    <Box
      onClick={readOnly ? undefined : onUploadClick}
      sx={{
        height: 200,
        width: 200,
        maxHeight: 250,
        maxWidth: "100%",
        borderRadius: 2,
        border: `2px dashed ${INPUT_COLOR_BORDER}`,
        backgroundColor: "action.hover",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        cursor: readOnly ? "default" : "pointer",
        transition: "all 0.2s",
        ...(!readOnly && {
          "&:hover": {
            borderColor: "text.primary",
            backgroundColor: "action.selected",
          },
        }),
      }}
    >
      <AddIcon
        sx={{
          fontSize: 48,
          color: "text.secondary",
        }}
      />
    </Box>
  );
};

export const LogoSection = ({ formMethods, hasError = false }) => {
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isLogoModalOpen, setIsLogoModalOpen] = useState(false);
  const [logoUrlModal, setLogoUrlModal] = useState(null);
  const dispatch = useDispatchApp();
  const { userID } = dispatch(getPathDatabaseByRole());
  const { t } = useTranslationApp();
  const readOnly = useBrandSetupReadOnly();

  if (!formMethods || !formMethods.control) {
    return null;
  }

  const { control, errors, setValue } = formMethods;

  const onOpenLogoModal = (url) => {
    if (!url) return;
    setLogoUrlModal(url);
    setIsLogoModalOpen(true);
  };

  const onCloseLogoModal = () => {
    setIsLogoModalOpen(false);
    setLogoUrlModal(null);
  };

  const onPlaceholderClick = () => {
    const input = document.getElementById("upload-logo-url");
    if (input) {
      input.click();
    }
  };

  return (
    <>
      <CardContainer title="" hasError={hasError}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Header */}
          <SectionHeader
            icon={ImageIcon}
            title={LOGO}
            subtitle={LOGO_DESCRIPTION}
          />

          {/* Error Message */}
          {errors["logoUrl"] && (
            <Typography
              variant="body2"
              color="error"
              sx={{ fontSize: "0.75rem" }}
            >
              {errors["logoUrl"].message}
            </Typography>
          )}

          {/* Content */}
          <Controller
            name="logoUrl"
            control={control}
            rules={{
              required: {
                value: false,
              },
            }}
            render={({ field }) => {
              return (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {field.value ? (
                    <LogoPreview
                      logoUrl={field.value}
                      onClick={() => onOpenLogoModal(field.value)}
                    />
                  ) : (
                    <LogoPlaceholder
                      onUploadClick={onPlaceholderClick}
                      readOnly={readOnly}
                    />
                  )}
                  {!readOnly && (
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <UploadFileButton
                        id="upload-logo-url"
                        textUpload={BUTTON_UPLOAD_LOGO}
                        colorUpload="secondary"
                        textRemove={BUTTON_CHANGE_LOGO}
                        disableVerificationMedia={true}
                        colorRemove="secondary"
                        value={field.value}
                        onChange={(value) => {
                          const url = value?.[0];
                          field.onChange(url);
                        }}
                        pathStorage={`socialMedia/${userID}/`}
                        acceptedFormats={LOGO_IMAGE_FORMATS}
                        modeUpload="sync"
                        loading={isUploadingLogo}
                        onCallbackLoading={(loading) =>
                          setIsUploadingLogo(loading)
                        }
                        sx={{
                          fontSize: FONT_SIZE_BUTTON,
                        }}
                      />
                      {field.value && (
                        <Button
                          onClick={() => field.onChange(null)}
                          color="secondary"
                          sx={{
                            fontSize: FONT_SIZE_BUTTON,
                          }}
                        >
                          {t(BUTTON_DELETE_LOGO)}
                        </Button>
                      )}
                    </Box>
                  )}
                </Box>
              );
            }}
          />
        </Box>
      </CardContainer>
      {isLogoModalOpen && (
        <ModalMedia
          modalOpen={isLogoModalOpen}
          onCloseModal={onCloseLogoModal}
          url={logoUrlModal}
          showEditButton={false}
          showShareButton={false}
          showDownloadButton={true}
          showDeleteButton={!readOnly}
          onDelete={async () => {
            setValue("logoUrl", null, {
              shouldDirty: true,
              shouldValidate: true,
            });
          }}
        />
      )}
    </>
  );
};

export default LogoSection;
